/**
 * Centralized PostHog Analytics Utility
 *
 * All event tracking flows through this module to ensure:
 * - Typed event names prevent typos
 * - Consistent property shapes
 * - Graceful no-op when PostHog isn't loaded
 * - Single place to audit all tracked events
 */
import posthog from 'posthog-js';

// ─── Helpers ───────────────────────────────────────────────

function isLoaded(): boolean {
  return typeof window !== 'undefined' && !!posthog && !!posthog.__loaded;
}

function capture(event: string, properties?: Record<string, unknown>) {
  if (!isLoaded()) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // Analytics should never break the app
  }
}

// ─── Identity ──────────────────────────────────────────────

export function identifyUser(userId: string, properties: {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  is_internal?: boolean;
  is_superadmin?: boolean;
}) {
  if (!isLoaded()) return;
  try {
    posthog.identify(userId, properties);
  } catch {
    // Silently fail
  }
}

export function setCompanyGroup(companyId: string, properties: {
  name?: string;
  industry?: string;
}) {
  if (!isLoaded()) return;
  try {
    posthog.group('company', companyId, properties);
  } catch {
    // Silently fail
  }
}

export function resetUser() {
  if (!isLoaded()) return;
  try {
    posthog.reset();
  } catch {
    // Silently fail
  }
}

// ─── Auth Events ───────────────────────────────────────────

export function trackLogout() {
  capture('user_logged_out');
}

// ─── Map Events ────────────────────────────────────────────

export function trackUnitSelected(unitId: string, unitCode: string, source: 'map' | 'list' | 'search') {
  capture('unit_selected', { unit_id: unitId, unit_code: unitCode, source });
}

export function trackMapSearch(queryLength: number, resultsCount: number) {
  capture('map_search_performed', { query_length: queryLength, results_count: resultsCount });
}

export function trackHvacUpdated(unitId: string) {
  capture('hvac_updated', { unit_id: unitId });
}

export function trackMapViewModeChanged(viewMode: string) {
  capture('map_view_mode_changed', { view_mode: viewMode });
}

// ─── Dashboard Events ──────────────────────────────────────

export function trackDashboardDateRange(dateRange: string) {
  capture('dashboard_date_range_changed', { date_range: dateRange });
}

export function trackDashboardLiveMode(enabled: boolean) {
  capture('dashboard_live_mode_toggled', { enabled });
}

// ─── Reports Events ────────────────────────────────────────

export function trackReportTabChanged(tab: string) {
  capture('report_tab_changed', { tab });
}

export function trackReportExported(properties: {
  format: string;
  group_count: number;
  date_range?: string;
}) {
  capture('report_exported', properties);
}

export function trackReportProductionDatesToggled(enabled: boolean) {
  capture('report_production_dates_toggled', { enabled });
}

export function trackReportCompareSlotAdded(slotIndex: number) {
  capture('report_compare_slot_added', { slot_index: slotIndex });
}

export function trackReportExcelDownloaded(success: boolean) {
  capture('report_excel_downloaded', { success });
}

// ─── Filter Events ─────────────────────────────────────────

export function trackFilterChanged(filterType: string, selectedCount: number, totalCount: number) {
  capture('filter_changed', {
    filter_type: filterType,
    selected_count: selectedCount,
    total_count: totalCount,
  });
}

// ─── Admin Events ──────────────────────────────────────────

export function trackAdminAction(entity: string, action: 'created' | 'updated' | 'deleted' | 'invited', entityId?: string) {
  capture(`admin_${entity}_${action}`, {
    entity_id: entityId,
  });
}
